<template>
  <div>
    <div class="d-flex align-center mb-4">
      <v-btn
        v-if="canCreate"
        color="primary"
        :loading="isCreating"
        @click="createShipment"
      >
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("STOCK.SHIPMENTS.ADD") }}
      </v-btn>
      <span v-else class="caption grey--text">
        {{ $t("STOCK.SHIPMENTS.NO_PERMISSION") }}
      </span>
    </div>

    <v-alert v-if="!items.length" type="info" dense text>
      {{ $t("STOCK.SHIPMENTS.EMPTY") }}
    </v-alert>

    <div v-else class="shipment-sets">
      <v-tabs
        v-model="activeSet"
        class="shipment-set-tabs"
        background-color="transparent"
        color="#1d7f72"
        height="56"
        show-arrows
      >
        <v-tab v-for="shipment in items" :key="shipment.id">
          <span class="shipment-set-tab">
            <span class="shipment-set-tab__title">{{ shipmentTitle(shipment) }}</span>
            <span v-if="shipmentTabMeta(shipment)" class="shipment-set-tab__meta">
              {{ shipmentTabMeta(shipment) }}
            </span>
          </span>
        </v-tab>
      </v-tabs>

      <div
        v-for="(shipment, index) in items"
        v-show="activeSet === index"
        :key="`set-${shipment.id}`"
        class="shipment-set-panel"
      >
          <div v-if="(shipment.delivery_notes || []).length" class="mb-3">
            <div class="caption grey--text mb-1">{{ $t("STOCK.SHIPMENTS.DELIVERY_NOTES") }}</div>
            <router-link
              v-for="note in shipment.delivery_notes"
              :key="`dn-${shipment.id}-${note.id}`"
              class="mr-3"
              :to="{ name: 'stock-delivery-notes-view', params: { id: note.id } }"
            >
              {{ note.code }}
            </router-link>
          </div>
          <v-row class="mb-2">
            <v-col cols="12" md="4">
              <v-text-field
                :value="shipment.label"
                :label="$t('STOCK.SHIPMENTS.LABEL')"
                :disabled="!canEdit"
                @change="updateLabel(shipment, $event)"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :value="shipment.notes"
                :label="$t('GENERAL.LABELS.NOTES')"
                :disabled="!canEdit"
                @change="updateNotes(shipment, $event)"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-btn
                v-if="canEdit"
                color="error"
                outlined
                small
                :loading="deletingId === shipment.id"
                @click="removeShipment(shipment)"
              >
                {{ $t("STOCK.SHIPMENTS.DELETE") }}
              </v-btn>
            </v-col>
          </v-row>

          <div class="shipment-doc-switch mb-4">
            <v-tabs
              v-model="shipment.tab"
              class="shipment-doc-switch__tabs"
              background-color="transparent"
              height="44"
            >
              <v-tab>
                <v-icon left small>mdi-file-document-outline</v-icon>
                {{ $t("STOCK.SHIPMENTS.TAB_CMR") }}
              </v-tab>
              <v-tab>
                <v-icon left small>mdi-file-excel-outline</v-icon>
                {{ $t("STOCK.SHIPMENTS.TAB_TRADE_DOCUMENT") }}
              </v-tab>
            </v-tabs>
          </div>
          <v-tabs-items v-model="shipment.tab">
            <v-tab-item eager>
              <div class="pt-3">
                <v-row class="mb-2">
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="shipment.cmr_document && shipment.cmr_document.cmr_number"
                      :label="$t('STOCK.CMR.NUMBER')"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="cmrStatusLabel(shipment.cmr_document && shipment.cmr_document.status)"
                      :label="$t('GENERAL.LABELS.STATUS')"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
                <div v-if="supportsCmr" class="d-flex align-center flex-wrap mb-3">
                  <v-btn
                    v-if="canEdit"
                    color="primary"
                    outlined
                    small
                    class="mr-2 mb-2"
                    :loading="generatingCmrId === shipment.id"
                    @click="generateCmr(shipment)"
                  >
                    <v-icon left small>mdi-file-word</v-icon>
                    {{ cmrFile(shipment) ? $t("STOCK.CMR.REGENERATE") : $t("STOCK.CMR.GENERATE") }}
                  </v-btn>
                  <v-btn
                    v-if="cmrFile(shipment)"
                    small
                    text
                    class="mb-2"
                    @click="downloadFile(shipment, cmrFile(shipment))"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    {{ $t("GENERAL.LABELS.DOWNLOAD") }}
                  </v-btn>
                </div>
                <v-alert v-else type="info" dense text class="mb-3">
                  {{ $t("STOCK.CMR.NOT_APPLICABLE") }}
                </v-alert>
                <StockCmrForm
                  :ref="'cmrForm-' + shipment.id"
                  :form="shipment.cmr_document && shipment.cmr_document.form"
                  :disabled="!canEdit"
                  :saving="savingCmrId === shipment.id"
                />
              </div>
            </v-tab-item>
            <v-tab-item eager>
              <div class="pt-3">
                <v-row class="mb-2">
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="shipment.trade_document && shipment.trade_document.document_number"
                      :label="$t('STOCK.TRADE_DOCUMENT.NUMBER')"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mb-3">
                  <v-btn
                    v-if="canEdit"
                    color="primary"
                    outlined
                    small
                    class="mr-2 mb-2"
                    :loading="generatingTradeId === shipment.id"
                    @click="generateTrade(shipment)"
                  >
                    <v-icon left small>mdi-file-excel</v-icon>
                    {{
                      tradeFile(shipment)
                        ? $t("STOCK.TRADE_DOCUMENT.REGENERATE")
                        : $t("STOCK.TRADE_DOCUMENT.GENERATE")
                    }}
                  </v-btn>
                  <v-btn
                    v-if="tradeFile(shipment)"
                    small
                    text
                    class="mb-2"
                    @click="downloadFile(shipment, tradeFile(shipment))"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    {{ $t("GENERAL.LABELS.DOWNLOAD") }}
                  </v-btn>
                </div>
                <StockTradeDocumentForm
                  :ref="'tradeForm-' + shipment.id"
                  :form="shipment.trade_document && shipment.trade_document.form"
                  :disabled="!canEdit"
                  :saving="savingTradeId === shipment.id"
                />
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
    </div>

    <v-dialog v-model="showGroupDialog" max-width="640">
      <v-card>
        <v-card-title>{{ $t("STOCK.SHIPMENTS.SELECT_DELIVERY_NOTES") }}</v-card-title>
        <v-card-text>
          <v-radio-group v-model="selectedGroupIndex">
            <v-radio
              v-for="(group, index) in shipmentGroups"
              :key="`group-${index}`"
              :value="index"
              :label="groupLabel(group)"
            />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showGroupDialog = false">{{ $t("GENERAL.LABELS.CANCEL") }}</v-btn>
          <v-btn color="primary" :disabled="selectedGroupIndex === null" :loading="isCreating" @click="confirmGroup">
            {{ $t("STOCK.SHIPMENTS.ADD") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import StockCmrForm from "@/views/partials/stock/shipments/StockCmrForm.vue";
import StockTradeDocumentForm from "@/views/partials/stock/shipments/StockTradeDocumentForm.vue";
import {
  CREATE_STOCK_SHIPMENT,
  GET_STOCK_SHIPMENT_GROUPS,
  DELETE_STOCK_SHIPMENT,
  DOWNLOAD_STOCK_SHIPMENT_FILE,
  GENERATE_STOCK_SHIPMENT_CMR,
  GENERATE_STOCK_SHIPMENT_TRADE_DOCUMENT,
  SAVE_STOCK_SHIPMENT_CMR,
  SAVE_STOCK_SHIPMENT_TRADE_DOCUMENT,
  UPDATE_STOCK_SHIPMENT,
} from "@/store/stock/stockShipments.module";
import { SHOW_SNACK } from "@/store/snack.module";

export default {
  name: "StockShipmentsPanel",
  components: { StockCmrForm, StockTradeDocumentForm },
  props: {
    transportOrderId: {
      type: Number,
      required: true,
    },
    shipments: {
      type: Array,
      default: () => [],
    },
    canCreate: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    supportsCmr: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      items: [],
      activeSet: 0,
      isCreating: false,
      deletingId: null,
      savingCmrId: null,
      savingTradeId: null,
      generatingCmrId: null,
      generatingTradeId: null,
      showGroupDialog: false,
      shipmentGroups: [],
      selectedGroupIndex: null,
    };
  },
  watch: {
    shipments: {
      immediate: true,
      handler(value) {
        if (value === this.items) {
          return;
        }
        const selectedId = this.items[this.activeSet] && this.items[this.activeSet].id;
        const tabsById = {};
        this.items.forEach((item) => {
          tabsById[item.id] = item.tab;
        });
        this.items = (value || []).map((shipment) => ({
          ...shipment,
          tab: tabsById[shipment.id] != null ? tabsById[shipment.id] : shipment.tab || 0,
        }));
        if (selectedId != null) {
          const nextIndex = this.items.findIndex((item) => item.id === selectedId);
          this.activeSet = nextIndex >= 0 ? nextIndex : 0;
        }
      },
    },
  },
  methods: {
    shipmentTitle(shipment) {
      const no = shipment.shipment_no;
      const label = shipment.label ? ` — ${shipment.label}` : "";
      return this.$t("STOCK.SHIPMENTS.TITLE_ITEM", { no }) + label;
    },
    shipmentTabMeta(shipment) {
      const cmr = shipment.cmr_document && shipment.cmr_document.cmr_number;
      const notes = (shipment.delivery_notes || []).map((note) => note.code);
      return [cmr, notes.join(", ")].filter(Boolean).join(" · ");
    },
    cmrStatusLabel(status) {
      const key = `STOCK.STATUSES.CMR.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status || "-";
    },
    cmrFile(shipment) {
      return (
        (shipment.cmr_document &&
          (shipment.cmr_document.generated_file || shipment.cmr_document.draft_pdf_file)) ||
        null
      );
    },
    tradeFile(shipment) {
      return (shipment.trade_document && shipment.trade_document.generated_file) || null;
    },
    replaceShipment(updated) {
      const index = this.items.findIndex((item) => item.id === updated.id);
      if (index === -1) {
        this.items.push({ ...updated, tab: 0 });
        return;
      }
      this.$set(this.items, index, {
        ...updated,
        tab: this.items[index].tab || 0,
      });
      this.$emit("updated", this.items);
    },
    groupLabel(group) {
      const notes = (group.delivery_notes || []).map((note) => note.code).join(", ");
      const company = group.company && (group.company.name || group.company.label);
      return [company, notes].filter(Boolean).join(" — ");
    },
    async createShipment() {
      this.isCreating = true;
      try {
        const groups = await this.$store.dispatch(GET_STOCK_SHIPMENT_GROUPS, this.transportOrderId);
        this.shipmentGroups = groups || [];
        if (!this.shipmentGroups.length) {
          await this.createShipmentFromGroup({ delivery_notes: [] });
          return;
        }
        if (this.shipmentGroups.length === 1) {
          await this.createShipmentFromGroup(this.shipmentGroups[0]);
          return;
        }
        this.selectedGroupIndex = 0;
        this.showGroupDialog = true;
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isCreating = false;
      }
    },
    async confirmGroup() {
      const group = this.shipmentGroups[this.selectedGroupIndex];
      if (!group) {
        return;
      }
      this.showGroupDialog = false;
      await this.createShipmentFromGroup(group);
    },
    async createShipmentFromGroup(group) {
      this.isCreating = true;
      try {
        const created = await this.$store.dispatch(CREATE_STOCK_SHIPMENT, {
          transportOrderId: this.transportOrderId,
          params: {
            delivery_note_ids: (group.delivery_notes || []).map((note) => note.id),
          },
        });
        this.items.push({ ...created, tab: 0 });
        this.activeSet = this.items.length - 1;
        this.$emit("updated", this.items);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isCreating = false;
      }
    },
    async updateLabel(shipment, label) {
      await this.patchShipment(shipment, { label });
    },
    async updateNotes(shipment, notes) {
      await this.patchShipment(shipment, { notes });
    },
    async patchShipment(shipment, params) {
      if (!this.canEdit) {
        return;
      }
      try {
        const updated = await this.$store.dispatch(UPDATE_STOCK_SHIPMENT, {
          id: shipment.id,
          params,
        });
        this.replaceShipment(updated);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      }
    },
    async removeShipment(shipment) {
      if (!window.confirm(this.$t("STOCK.SHIPMENTS.CONFIRM_DELETE"))) {
        return;
      }
      this.deletingId = shipment.id;
      try {
        await this.$store.dispatch(DELETE_STOCK_SHIPMENT, shipment.id);
        const removedIndex = this.items.findIndex((item) => item.id === shipment.id);
        this.items = this.items.filter((item) => item.id !== shipment.id);
        if (removedIndex !== -1 && removedIndex < this.activeSet) {
          this.activeSet -= 1;
        }
        if (this.activeSet >= this.items.length) {
          this.activeSet = Math.max(0, this.items.length - 1);
        }
        this.$emit("updated", this.items);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.deletingId = null;
      }
    },
    formComponent(refName) {
      const formRef = this.$refs[refName];
      return Array.isArray(formRef) ? formRef[0] : formRef;
    },
    collectDirtyDocuments() {
      if (!this.canEdit) {
        return [];
      }
      const pending = [];
      this.items.forEach((shipment, index) => {
        [
          { kind: "cmr", tab: 0, refName: "cmrForm-" + shipment.id },
          { kind: "trade", tab: 1, refName: "tradeForm-" + shipment.id },
        ].forEach((doc) => {
          const form = this.formComponent(doc.refName);
          if (!form || typeof form.isDirty !== "function" || !form.isDirty()) {
            return;
          }
          pending.push({
            shipmentId: shipment.id,
            shipmentNo: shipment.shipment_no,
            index,
            kind: doc.kind,
            tab: doc.tab,
            payload: form.payload(),
          });
        });
      });
      return pending;
    },
    async persistDirtyDocuments(pending) {
      const failures = [];
      for (const doc of pending || []) {
        const shipment =
          this.items.find((item) => item.id === doc.shipmentId) || { id: doc.shipmentId };
        try {
          if (doc.kind === "cmr") {
            await this.persistCmr(shipment, doc.payload);
          } else {
            await this.persistTrade(shipment, doc.payload);
          }
        } catch (errors) {
          failures.push({
            ...doc,
            message: this.getErrorMessage(errors),
          });
        }
      }
      return failures;
    },
    focusFailedDocument(failure) {
      const index = this.items.findIndex((item) => item.id === failure.shipmentId);
      if (index >= 0) {
        this.activeSet = index;
        this.$set(this.items[index], "tab", failure.tab);
      }
      this.$nextTick(() => {
        const form = this.formComponent(
          (failure.kind === "cmr" ? "cmrForm-" : "tradeForm-") + failure.shipmentId
        );
        const element = form && form.$el;
        if (element && element.scrollIntoView) {
          element.scrollIntoView({ block: "start" });
        }
      });
    },
    async persistCmr(shipment, form) {
      this.savingCmrId = shipment.id;
      try {
        const updated = await this.$store.dispatch(SAVE_STOCK_SHIPMENT_CMR, {
          id: shipment.id,
          params: { form },
        });
        this.replaceShipment(updated);
        await this.$nextTick();
      } finally {
        this.savingCmrId = null;
      }
    },
    async persistTrade(shipment, form) {
      this.savingTradeId = shipment.id;
      try {
        const updated = await this.$store.dispatch(SAVE_STOCK_SHIPMENT_TRADE_DOCUMENT, {
          id: shipment.id,
          params: { form },
        });
        this.replaceShipment(updated);
        await this.$nextTick();
      } finally {
        this.savingTradeId = null;
      }
    },
    async generateCmr(shipment) {
      this.generatingCmrId = shipment.id;
      try {
        const form = this.formComponent("cmrForm-" + shipment.id);
        if (form && typeof form.isDirty === "function" && form.isDirty()) {
          await this.persistCmr(shipment, form.payload());
        }
        const updated = await this.$store.dispatch(GENERATE_STOCK_SHIPMENT_CMR, shipment.id);
        this.replaceShipment(updated);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.getErrorMessage(errors),
          color: "error",
        });
      } finally {
        this.generatingCmrId = null;
      }
    },
    async generateTrade(shipment) {
      this.generatingTradeId = shipment.id;
      try {
        const form = this.formComponent("tradeForm-" + shipment.id);
        if (form && typeof form.payload === "function") {
          await this.persistTrade(shipment, form.payload());
        }
        const updated = await this.$store.dispatch(
          GENERATE_STOCK_SHIPMENT_TRADE_DOCUMENT,
          shipment.id
        );
        this.replaceShipment(updated);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.getErrorMessage(errors),
          color: "error",
        });
      } finally {
        this.generatingTradeId = null;
      }
    },
    downloadFile(shipment, file) {
      if (!file || !file.id) {
        return;
      }
      this.$store
        .dispatch(DOWNLOAD_STOCK_SHIPMENT_FILE, {
          id: shipment.id,
          fileId: file.id,
          originalName: file.original_name,
          name: file.name,
        })
        .catch((errors) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(errors),
            color: "error",
          });
        });
    },
    getErrorMessage(errors) {
      if (!errors) {
        return this.$t("GENERAL.UNEXPECTED_ERROR");
      }
      if (typeof errors === "string") {
        return errors;
      }
      if (errors.message) {
        return errors.message;
      }
      if (errors.errors) {
        const firstKey = Object.keys(errors.errors)[0];
        if (firstKey && errors.errors[firstKey][0]) {
          return errors.errors[firstKey][0];
        }
      }
      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
  },
};
</script>

<style scoped>
.shipment-sets {
  border: 1px solid #d5e3e0;
  border-radius: 8px;
  background: #fff;
}

.shipment-set-tabs {
  border-bottom: 1px solid #d5e3e0;
  background: #f4faf8;
  border-radius: 8px 8px 0 0;
}

.shipment-set-tabs ::v-deep .v-tab {
  text-transform: none;
  letter-spacing: 0;
  height: 56px;
  max-width: 280px;
}

.shipment-set-tabs ::v-deep .v-tab--active {
  background: #fff;
}

.shipment-set-tab {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
  min-width: 0;
  max-width: 240px;
}

.shipment-set-tab__title {
  font-weight: 600;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
}

.shipment-set-tab__meta {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 400;
  color: #5b6b68;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
}

.shipment-set-panel {
  padding: 16px 16px 4px;
}

.shipment-set-panel ::v-deep .shipment-document-form {
  scroll-margin-top: 140px;
}

.shipment-doc-switch {
  display: inline-flex;
  max-width: 100%;
  padding: 4px;
  background: #e8f1ef;
  border: 1px solid #c5d8d4;
  border-radius: 12px;
}

.shipment-doc-switch__tabs {
  max-width: 100%;
}

.shipment-doc-switch__tabs ::v-deep .v-tabs-bar {
  height: 40px;
  background: transparent !important;
}

.shipment-doc-switch__tabs ::v-deep .v-tabs-slider-wrapper {
  display: none;
}

.shipment-doc-switch__tabs ::v-deep .v-tab {
  min-width: 180px;
  margin: 0 2px;
  padding: 0 16px;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  color: #5b6b68;
}

.shipment-doc-switch__tabs ::v-deep .v-tab:hover {
  background: rgba(255, 255, 255, 0.55);
}

.shipment-doc-switch__tabs ::v-deep .v-tab--active {
  background: #fff;
  color: #1d7f72;
  box-shadow: 0 1px 4px rgba(29, 127, 114, 0.18);
}

.shipment-doc-switch__tabs ::v-deep .v-icon {
  color: inherit;
}
</style>
